import { getColor } from "@faire/sdui/lib/getColor";
import { getIcon } from "@faire/sdui/lib/getIcon";
import {
  Button,
  ButtonIconPosition,
  ButtonSize,
} from "@faire/slate/components/button";
import { IButtonUIElement } from "@faire/web-api--source/indigofair/data/IButtonUIElement";
import React from "react";

type ButtonProps = React.ComponentPropsWithoutRef<typeof Button>;

export const mapButtonUIElementToButtonProps = (
  buttonUIElement: IButtonUIElement
): ButtonProps => {
  const iconProps = getButtonIconProps(buttonUIElement);

  if (!buttonUIElement.label) {
    throw new Error("Button label is required");
  }

  return {
    variant: getButtonVariant(buttonUIElement.variant),
    destructive: buttonUIElement.destructive,
    children: buttonUIElement.label,
    size: getButtonSize(buttonUIElement.size),
    iconProps,
  };
};

const getButtonIconProps = (
  buttonUIElement?: IButtonUIElement
): ButtonProps["iconProps"] | undefined => {
  if (!buttonUIElement?.icon) {
    return undefined;
  }

  const Icon = buttonUIElement.icon ? getIcon(buttonUIElement.icon) : undefined;

  return Icon
    ? {
        Component: (props) => (
          <Icon
            {...props}
            color={getColor(buttonUIElement?.icon?.color_token) ?? props.color}
          />
        ),
        position: getButtonIconPosition(buttonUIElement.icon_position),
      }
    : undefined;
};

// TODO: support padded tertiary variant after SDUI_SWAP_TERTIARY_TO_PLAIN_250911
const getButtonVariant = (
  variant: keyof typeof IButtonUIElement.ButtonVariant | undefined
): ButtonProps["variant"] | undefined => {
  switch (variant) {
    case IButtonUIElement.ButtonVariant.PRIMARY:
      return "primary";
    case IButtonUIElement.ButtonVariant.SECONDARY:
      return "secondary";
    case IButtonUIElement.ButtonVariant.PLAIN:
    case IButtonUIElement.ButtonVariant.TERTIARY:
      return "plain";
    default:
      return undefined;
  }
};

const getButtonIconPosition = (
  iconPosition: keyof typeof IButtonUIElement.ButtonIconPosition | undefined
): ButtonIconPosition | undefined => {
  switch (iconPosition) {
    case IButtonUIElement.ButtonIconPosition.START:
      return "start";
    case IButtonUIElement.ButtonIconPosition.END:
      return "end";
    case IButtonUIElement.ButtonIconPosition.BUTTON_ICON_POSITION_UNKNOWN:
    default:
      return undefined;
  }
};

const getButtonSize = (
  size: keyof typeof IButtonUIElement.ButtonSize | undefined
): ButtonSize | undefined => {
  switch (size) {
    case IButtonUIElement.ButtonSize.SMALL:
      return "small";
    case IButtonUIElement.ButtonSize.MEDIUM:
      return "medium";
    default:
      return undefined;
  }
};
